import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Space,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Tooltip,
  Layout,
  Drawer,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import {
  fetchBlacklistedVehicles,
  addVehicleToBlacklist,
  updateBlacklistedVehicle,
  deleteBlacklistedVehicle,
  fetchTrashedBlacklistedVehicles,
} from "../api/blacklist_vehicle/blacklistRequest"; // Adjust path as needed
import { countries } from "../utils/countries"; // Adjust path as needed
import dayjs from "dayjs"; // Use dayjs instead of moment
import PermissionRender from "../components/PermissionRender"; // Assuming you have this component

const { Option } = Select;
const { Content } = Layout;

const VehicleBlacklistPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [trashedVehicles, setTrashedVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [trashedPagination, setTrashedPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({});
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();

  const fetchData = useCallback(
    async (page = 1, pageSize = 15, currentFilters = {}) => {
      setLoading(true);
      try {
        const params = {
          page: page,
          per_page: pageSize,
          ...currentFilters, // Include matricule and country filters
        };
        const data = await fetchBlacklistedVehicles(params); // Ensure your API function accepts params
        setVehicles(data.data);
        setPagination({
          current: data.current_page,
          pageSize: data.per_page,
          total: data.total,
        });
      } catch (error) {
        message.error(
          "Erreur lors de la récupération des véhicules sur liste noire."
        );
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    },
    []
  ); // Dependencies for useCallback

  const fetchTrashedData = useCallback(async (page = 1, pageSize = 10) => {
    setSidebarLoading(true);
    try {
      const params = {
        page: page,
        per_page: pageSize,
      };
      const data = await fetchTrashedBlacklistedVehicles(params);
      setTrashedVehicles(data.data);
      setTrashedPagination({
        current: data.current_page,
        pageSize: data.per_page,
        total: data.total,
      });
    } catch (error) {
      message.error(
        "Erreur lors de la récupération de l'historique des véhicules supprimés."
      );
      console.error("Fetch trashed error:", error);
    } finally {
      setSidebarLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(pagination.current, pagination.pageSize, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData]); // Trigger fetch when fetchData changes (due to filters, pagination)

  const handleTableChange = (newPagination, filters, sorter) => {
    // You might need to adapt this if your backend supports sorting/filtering via API
    fetchData(newPagination.current, newPagination.pageSize, filters);
  };

  const handleSearch = (values) => {
    setFilters(values);
    console.log(values);
    fetchData(1, pagination.pageSize, values); // Reset to page 1 on new search
  };

  const resetSearch = () => {
    searchForm.resetFields();
    setFilters({});
    fetchData(1, pagination.pageSize, {}); // Reset to page 1 and clear filters
  };

  const showDeletedHistory = () => {
    setIsSidebarVisible(true);
    fetchTrashedData(1, 10); // Fetch trashed data when sidebar opens
  };

  const closeSidebar = () => {
    setIsSidebarVisible(false);
  };

  const handleTrashedTableChange = (newPagination) => {
    fetchTrashedData(newPagination.current, newPagination.pageSize);
  };

  const showAddModal = () => {
    setEditingVehicle(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditingVehicle(record);
    console.log(record);
    form.setFieldsValue({
      ...record,
      // Use dayjs for parsing the date
      expires_at: record.expires_at ? dayjs(record.expires_at) : null,
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingVehicle(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const dataToSend = {
        ...values,
        // Use dayjs for formatting the date
        expires_at: values.expires_at
          ? values.expires_at.format("YYYY-MM-DD")
          : null,
      };

      setLoading(true);
      if (editingVehicle) {
        await updateBlacklistedVehicle(editingVehicle.id, dataToSend);
        message.success("Véhicule mis à jour avec succès.");
      } else {
        await addVehicleToBlacklist(dataToSend);
        message.success("Véhicule ajouté à la liste noire avec succès.");
      }
      setIsModalVisible(false);
      fetchData(pagination.current, pagination.pageSize, filters); // Refresh data
    } catch (error) {
      console.error("Failed:", error);
      const errorMessage =
        error.response?.data?.message || "Une erreur est survenue.";
      message.error(`Échec : ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await deleteBlacklistedVehicle(id);
      message.success("Véhicule retiré de la liste noire avec succès.");
      fetchData(pagination.current, pagination.pageSize, filters); // Refresh data
    } catch (error) {
      message.error("Erreur lors de la suppression du véhicule.");
      console.error("Delete error:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Matricule",
      dataIndex: "matricule",
      key: "matricule",
      sorter: (a, b) => a.matricule.localeCompare(b.matricule),
    },
    {
      title: "Pays",
      dataIndex: "country",
      key: "country",
      render: (code) => countries.find((c) => c.code === code)?.name || code,
      filters: countries.map((c) => ({ text: c.name, value: c.code })),
      onFilter: (value, record) => record.country === value,
    },
    {
      title: "Raison",
      dataIndex: "reason",
      key: "reason",
    },
    {
      title: "Expire le",
      dataIndex: "expires_at",
      key: "expires_at",
      // Use dayjs for rendering and sorting
      render: (text) => (text ? dayjs(text).format("DD/MM/YYYY") : "Jamais"),
      sorter: (a, b) =>
        dayjs(a.expires_at || 0).unix() - dayjs(b.expires_at || 0).unix(),
    },
    {
      title: "Créé le",
      dataIndex: "created_at",
      key: "created_at",
      // Use dayjs for rendering and sorting
      render: (text) => dayjs(text).format("DD/MM/YYYY HH:mm"),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <PermissionRender
            requiredPermission={["blacklist_vehicles", "update"]}
          >
            <Tooltip title="Modifier">
              <Button
                icon={<EditOutlined />}
                onClick={() => showEditModal(record)}
                size="small"
              />
            </Tooltip>
          </PermissionRender>
          <PermissionRender
            requiredPermission={["blacklist_vehicles", "delete"]}
          >
            <Popconfirm
              title="Êtes-vous sûr de vouloir retirer ce véhicule de la liste noire ?"
              onConfirm={() => handleDelete(record.id)}
              okText="Oui"
              cancelText="Non"
            >
              <Tooltip title="Retirer de la liste noire">
                <Button icon={<DeleteOutlined />} danger size="small" />
              </Tooltip>
            </Popconfirm>
          </PermissionRender>
        </Space>
      ),
    },
  ];

  const trashedColumns = [
    {
      title: "Matricule",
      dataIndex: "matricule",
      key: "matricule",
      width: 120,
    },
    {
      title: "Pays",
      dataIndex: "country",
      key: "country",
      width: 80,
      render: (code) => countries.find((c) => c.code === code)?.name || code,
    },
    {
      title: "Raison",
      dataIndex: "reason",
      key: "reason",
      width: 150,
      render: (text) => (
        <Tooltip title={text}>
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "130px",
            }}
          >
            {text || "N/A"}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Créé le",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => dayjs(text).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Supprimé le",
      dataIndex: "deleted_at",
      key: "deleted_at",
      width: 130,
      render: (text) => dayjs(text).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Supprimé par",
      dataIndex: "deleted_by",
      key: "deleted_by",
      width: 120,
      render: (deletedBy) => (
        <Tooltip title={`ID Agent: ${deletedBy?.id_agent}`}>
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100px",
            }}
          >
            {deletedBy?.full_name || "N/A"}
          </div>
        </Tooltip>
      ),
    },
  ];

  return (
    <Layout className="min-h-screen ">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card>
          <Row
            gutter={[16, 16]}
            justify="space-between"
            align="middle"
            style={{ marginBottom: 16 }}
          >
            <Col>
              <h2>Liste Noire des Véhicules</h2>
            </Col>
            <Col>
              <Space>
                <PermissionRender
                  requiredPermission={["blacklist_vehicles", "create"]}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={showAddModal}
                  >
                    Ajouter un Véhicule
                  </Button>
                </PermissionRender>
                <PermissionRender
                  requiredPermission={["blacklist_vehicles", "read_history"]}
                >
                  <Button
                    icon={<HistoryOutlined />}
                    onClick={showDeletedHistory}
                  >
                    Historique
                  </Button>
                </PermissionRender>
                <Tooltip title="Rafraîchir">
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() =>
                      fetchData(
                        pagination.current,
                        pagination.pageSize,
                        filters
                      )
                    }
                    loading={loading}
                  />
                </Tooltip>
              </Space>
            </Col>
          </Row>

          {/* Search Form */}
          <Form
            form={searchForm}
            layout="inline"
            onFinish={handleSearch}
            style={{ marginBottom: 16 }}
          >
            <Form.Item name="matricule">
              <Input
                placeholder="Rechercher par matricule"
                prefix={<SearchOutlined />}
                allowClear
              />
            </Form.Item>
            <Form.Item name="country">
              <Select
                placeholder="Filtrer par pays"
                style={{ width: 150 }}
                allowClear
              >
                {countries.map((country) => (
                  <Option key={country.code} value={country.code}>
                    {country.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Rechercher
              </Button>
            </Form.Item>
            <Form.Item>
              <Button onClick={resetSearch}>Réinitialiser</Button>
            </Form.Item>
          </Form>

          <Table
            columns={columns}
            dataSource={vehicles}
            rowKey="id"
            loading={loading}
            pagination={{ ...pagination, showSizeChanger: true }}
            onChange={handleTableChange}
            scroll={{ x: "max-content" }}
          />

          <Modal
            title={
              editingVehicle ? "Modifier le Véhicule" : "Ajouter un Véhicule à la Liste Noire"
            }
            visible={isModalVisible}
            onOk={handleOk}
            onCancel={handleCancel}
            confirmLoading={loading}
            destroyOnClose
          >
            <Form form={form} layout="vertical" name="blacklist_form">
              <Form.Item
                name="matricule"
                label="Matricule"
                className="mb-0"
                rules={[
                  { required: true, message: "Veuillez entrer la matricule !" },
                ]}
              >
                <Input placeholder="1234-A-56" />
              </Form.Item>
              <div className="text-xs text-gray-500 mt-1 mb-4">
                Exemple: 12345-A-1
              </div>
              <Form.Item
                name="country"
                label="Pays"
                rules={[
                  {
                    required: true,
                    message: "Veuillez sélectionner le pays !",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Sélectionner un pays"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {countries.map((country) => (
                    <Option key={country.code} value={country.code}>
                      {country.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="reason" label="Raison (Optionnel)">
                <Input.TextArea rows={3} placeholder="Raison de l'ajout à la liste noire..." />
              </Form.Item>
              <Form.Item
                name="expires_at"
                label="Date d'expiration (Optionnel)"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      // Use dayjs for validation
                      if (!value || value.isAfter(dayjs().subtract(1, "day"))) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          "La date d'expiration doit être aujourd'hui ou une date future."
                        )
                      );
                    },
                  }),
                ]}
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Form>
          </Modal>

          {/* Sidebar for Deleted Vehicles History */}
          <Drawer
            title="Historique des véhicules retirés de la liste noire"
            placement="right"
            closable={true}
            onClose={closeSidebar}
            visible={isSidebarVisible}
            width={900}
            destroyOnClose
          >
            <div style={{ marginBottom: 16 }}>
              <span style={{ fontSize: "14px", color: "#666" }}>
                Cet historique montre tous les véhicules qui ont été retirés
                de la liste noire.
              </span>
            </div>
            <Table
              columns={trashedColumns}
              dataSource={trashedVehicles}
              rowKey="id"
              loading={sidebarLoading}
              pagination={{
                ...trashedPagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} sur ${total} véhicules supprimés`,
              }}
              onChange={handleTrashedTableChange}
              scroll={{ x: "max-content" }}
              size="small"
            />
          </Drawer>
        </Card>
      </Content>
    </Layout>
  );
};

export default VehicleBlacklistPage;
